import { Typography } from '@material-ui/core';
import classNames from 'classnames';
import React from 'react';
import { useIntl } from 'react-intl';

import globalMessages from 'shared/global/messages';

import { ConversationTranscript, ConversationTurn } from '../api';
import { messages } from '../messages';
import { useStyles } from './styles';

type RichStep = Extract<
  ConversationTurn['reply'][number],
  { type: 'rich' }
>['step'];
type TranscriptRichStep = Extract<
  NonNullable<
    ConversationTranscript['turns'][number]['messages'][number]['part']
  >,
  { type: 'rich' }
>['step'];

const labels = {
  openings: messages.richStepOpenings,
  openings_nearby: messages.richStepOpeningsNearby,
  application_form: messages.richStepApplicationForm,
  question: messages.richStepQuestion,
  application_submitted: messages.richStepApplicationSubmitted,
  other: messages.transcriptStepPlaceholder,
};

export const RichStepCompact = ({
  step,
  variant = 'compact',
  openings = {},
}: {
  step: RichStep | TranscriptRichStep;
  variant?: 'compact' | 'full';
  openings?: ConversationTranscript['openings'];
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const text =
    step.kind === 'question'
      ? step.question
      : variant === 'compact' && 'message' in step
        ? step.message
        : null;
  const cards =
    step.kind === 'openings' && 'opening_ids' in step
      ? step.opening_ids.map(id => ({
          id,
          title: openings[id]?.title,
          location: openings[id]?.location,
        }))
      : 'openings' in step
        ? step.openings
        : [];

  return (
    <div
      className={classNames(classes.richStep, {
        [classes.richStepFull]: variant === 'full',
      })}
    >
      <Typography component="div" className={classes.richStepKind}>
        {intl.formatMessage(labels[step.kind], {
          stepName: 'tool_name' in step ? (step.tool_name ?? '') : '',
        })}
      </Typography>
      {step.kind === 'openings'
        ? cards?.map(opening => (
            <div key={opening.id} className={classes.richOpening}>
              <div>
                <Typography
                  component="div"
                  variant="h4"
                  className={classes.primary}
                >
                  {opening.title ?? intl.formatMessage(messages.unknownOpening)}
                </Typography>
                {opening.location != null && (
                  <Typography
                    component="div"
                    variant="body2"
                    className={classes.secondary}
                  >
                    {opening.location}
                  </Typography>
                )}
              </div>
              {variant === 'full' && (
                <Typography variant="body2" className={classes.muted}>
                  {intl.formatMessage(globalMessages.apply)}
                </Typography>
              )}
            </div>
          ))
        : step.kind !== 'other' &&
          text && (
            <Typography
              className={
                variant === 'full'
                  ? classes.richFullText
                  : classNames(classes.paragraph, classes.richStepText)
              }
            >
              {text}
            </Typography>
          )}
    </div>
  );
};
